
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Plus, ChevronUp, ChevronDown } from 'lucide-react';
import { AddTopBreederDialog } from './AddTopBreederDialog';

interface TopBreeder {
  id: string;
  breeder_id: string;
  display_order: number;
  profiles: {
    first_name: string;
    last_name: string;
    business_name: string | null;
    profile_image_url: string | null;
  };
}

export const TopBreedersSection = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: topBreeders, isLoading } = useQuery({
    queryKey: ['admin-top-breeders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('top_breeders')
        .select(`
          id,
          breeder_id,
          display_order,
          profiles!top_breeders_breeder_id_fkey (
            first_name,
            last_name,
            business_name,
            profile_image_url
          )
        `)
        .order('display_order');

      if (error) throw error;
      return data as TopBreeder[];
    },
  });

  const moveTopBreeder = useMutation({
    mutationFn: async ({ id, direction }: { id: string; direction: 'up' | 'down' }) => {
      if (!topBreeders) return;
      
      const currentIndex = topBreeders.findIndex(b => b.id === id);
      if (currentIndex === -1) return;
      
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= topBreeders.length) return;
      
      const currentBreeder = topBreeders[currentIndex];
      const targetBreeder = topBreeders[targetIndex];
      
      // Swap display orders
      const { error: error1 } = await supabase
        .from('top_breeders')
        .update({ display_order: targetBreeder.display_order })
        .eq('id', currentBreeder.id);
      
      if (error1) throw error1;
      
      const { error: error2 } = await supabase
        .from('top_breeders')
        .update({ display_order: currentBreeder.display_order })
        .eq('id', targetBreeder.id);
      
      if (error2) throw error2;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-top-breeders'] });
      toast({
        title: "Success",
        description: "Display order updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update display order.",
        variant: "destructive",
      });
    },
  });

  const removeTopBreeder = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('top_breeders')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-top-breeders'] });
      toast({
        title: "Success",
        description: "Top breeder removed successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to remove top breeder.",
        variant: "destructive",
      });
    },
  });

  const getInitials = (topBreeder: TopBreeder) => {
    return topBreeder.profiles.first_name.charAt(0).toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold mb-2">Top Breeders</h2>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold mb-2">Top Breeders</h2>
          <p className="text-gray-600">Manage featured breeders shown on the homepage</p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Top Breeder
        </Button>
      </div>

      <div className="grid gap-4">
        {topBreeders?.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-center text-gray-500">No top breeders selected yet.</p>
            </CardContent>
          </Card>
        ) : (
          topBreeders?.map((topBreeder, index) => (
            <Card key={topBreeder.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center">
                        {topBreeder.profiles.profile_image_url ? (
                          <img
                            src={topBreeder.profiles.profile_image_url}
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-400 via-green-500 to-green-600 flex items-center justify-center">
                            <span className="text-white text-sm font-bold">
                              {getInitials(topBreeder)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium">
                        {topBreeder.profiles.first_name} {topBreeder.profiles.last_name}
                      </h3>
                      {topBreeder.profiles.business_name && (
                        <p className="text-sm text-gray-600">{topBreeder.profiles.business_name}</p>
                      )}
                      <p className="text-xs text-gray-500">Position: {topBreeder.display_order}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => moveTopBreeder.mutate({ id: topBreeder.id, direction: 'up' })}
                      disabled={index === 0 || moveTopBreeder.isPending}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => moveTopBreeder.mutate({ id: topBreeder.id, direction: 'down' })}
                      disabled={index === (topBreeders?.length || 0) - 1 || moveTopBreeder.isPending}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeTopBreeder.mutate(topBreeder.id)}
                      disabled={removeTopBreeder.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <AddTopBreederDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['admin-top-breeders'] });
        }}
      />
    </div>
  );
};
